import { withResolvedError } from '@wix/members-area-commons-ts';
import type { FlowAPI } from '@wix/yoshi-flow-editor';

import {
  fetchAllBadges,
  getInitialData as getInitialDataRequest,
  loadMoreBlogPosts,
  refetchMemberBadges,
  updateAbout,
} from '../server/profile.api';
import type {
  GetAllBadgesResponse,
  GetBlogPostsResponse,
  GetInitialDataResponse,
  GetMemberBadgesResponse,
  InitialDataRequest,
  LoadMoreBlogPostsOptions,
  MemberAbout,
  MembersAboutMiddlewareService as IMembersAboutMiddlewareService,
  UpdateAboutResponse,
} from '../types';
import { Experiment } from '../types';

type ErrorHandler = FlowAPI['errorHandler'];
type HttpClient = FlowAPI['httpClient'];
type Experiments = FlowAPI['experiments'];

type MembersAboutMiddlewareServiceOptions = {
  httpClient: HttpClient;
  errorHandler: ErrorHandler;
  experiments: Experiments;
};

export class MembersAboutMiddlewareService
  implements IMembersAboutMiddlewareService
{
  private readonly httpClient: HttpClient;
  private readonly errorHandler: ErrorHandler;
  private readonly experiments: Experiments;

  constructor({
    httpClient,
    errorHandler,
    experiments,
  }: MembersAboutMiddlewareServiceOptions) {
    this.httpClient = httpClient;
    this.errorHandler = errorHandler;
    this.experiments = experiments;
  }

  async getInitialData(options: InitialDataRequest) {
    const requestFn = () =>
      this.experiments.enabled(Experiment.EnableAboutMiddleware)
        ? this.httpClient.get<GetInitialDataResponse>(
            `/_serverless/members-about-middleware/initial-data`,
            { params: options },
          )
        : this.httpClient.request(getInitialDataRequest(options));

    const { data } = await withResolvedError(requestFn, this.errorHandler, {
      errorCodesMap: {},
    });

    return data;
  }

  async updateAbout(about: MemberAbout) {
    const requestFn = () =>
      this.experiments.enabled(Experiment.EnableAboutMiddleware)
        ? this.httpClient.post<UpdateAboutResponse>(
            `/_serverless/members-about-middleware/update-about`,
            { about },
          )
        : this.httpClient.request(updateAbout({ about }));

    const { data } = await withResolvedError(requestFn, this.errorHandler, {
      errorCodesMap: {},
    });

    return data;
  }

  async getAllBadges() {
    const requestFn = () =>
      this.experiments.enabled(Experiment.EnableAboutMiddleware)
        ? this.httpClient.get<GetAllBadgesResponse>(
            `/_serverless/members-about-middleware/badges/all`,
          )
        : this.httpClient.request(fetchAllBadges());

    const { data } = await withResolvedError(requestFn, this.errorHandler, {
      errorCodesMap: {},
    });

    return data;
  }

  async getMemberBadges(assignedBadgeIds: string[]) {
    const requestFn = () =>
      this.experiments.enabled(Experiment.EnableAboutMiddleware)
        ? this.httpClient.post<GetMemberBadgesResponse>(
            `/_serverless/members-about-middleware/badges/member`,
            { assignedBadgeIds },
          )
        : this.httpClient.request(refetchMemberBadges({ assignedBadgeIds }));

    const { data } = await withResolvedError(requestFn, this.errorHandler, {
      errorCodesMap: {},
    });

    return data;
  }

  async getBlogPosts(options: LoadMoreBlogPostsOptions) {
    const requestFn = () =>
      this.experiments.enabled(Experiment.EnableAboutMiddleware)
        ? this.httpClient.get<GetBlogPostsResponse>(
            `/_serverless/members-about-middleware/blog-posts`,
            { params: options },
          )
        : this.httpClient.request(loadMoreBlogPosts(options));

    const { data } = await withResolvedError(requestFn, this.errorHandler, {
      errorCodesMap: {},
    });

    return data;
  }
}
